import { PUBLIC_AUTHORIZATION, TOKEN_URL } from "@/utils/apiUrls";
import { store } from "@/utils/atoms";
import { accessTokenAtom, refreshTokenAtom } from "@/utils/atoms";
import { anonymousLoginStatus } from "@/utils/authentication";
import { createReferenceId } from "@/utils/initializeSentry";

/**
 * Ensures the PUBLIC token is fresh.
 * - If not anonymous → switch to anonymous mode.
 * - If already anonymous → try to refresh token.
 * - If refresh fails → throw → caller decides how to handle.
 */
export const ensurePublicTokenIsFresh = async (): Promise<void> => {
  const refreshToken = store.get(refreshTokenAtom);

  // 1: If we are not anonymous → switch to anonymous mode now
  if (refreshToken !== anonymousLoginStatus) {
    await fetchPublicTokenAndSetAtoms();

    return;
  }

  // 2: Already anonymous → just fetch new token again
  await fetchPublicTokenAndSetAtoms();
};

const fetchPublicTokenAndSetAtoms = async () => {
  const referenceId = createReferenceId();
  const response = await fetch(
    `${TOKEN_URL.toString()}?grant_type=client_credentials&scope=all`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: PUBLIC_AUTHORIZATION,
        "X-Reference-Id": referenceId,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch public token: ${response.status}`);
  }

  const json = await response.json();

  if (!json.access_token) {
    throw new Error("Failed to refresh public token");
  }

  store.set(accessTokenAtom, json.access_token);
  store.set(refreshTokenAtom, anonymousLoginStatus);
};
